import React from 'react';
import Typography from '@mui/material/Typography';
import Grid from '@mui/material/Grid';
import Paper from '@mui/material/Paper';
import TextField from '@mui/material/TextField';
import Divider from '@mui/material/Divider';

import type { InterventionCenterGovStatistics } from '../../../common/types/statistics/fhbfStatistics';

type Props = {
  statistics: InterventionCenterGovStatistics;
};

function Case({ statistics }: Props) {
  return (
    <Paper
      elevation={0}
      sx={{
        display: 'block',
        padding: '0px 24px 20px 24px',
        maxWidth: '650px',
        minWidth: '300px',
      }}
    >
      <Grid container rowSpacing={1}>
        <Grid item xs={12}>
          <Typography fontSize='0.9rem' fontWeight='bold'>
            3. Beratung der von Gewalt betroffenen Frauen
          </Typography>
        </Grid>
        <Grid item xs={7}>
          <Typography fontSize='0.9rem' fontWeight='bold'>
            3.1 Anzahl der Frauen, die beraten wurden
          </Typography>
        </Grid>
        <Grid item xs={5}>
          <Typography fontSize='0.9rem' align='right' border='2px solid black' px='5px'>
            {statistics.numberOfWomenAdvised}
          </Typography>
        </Grid>
        <Grid item xs={12}>
          <Typography fontSize='0.75rem'>
            <b>Hinweis:</b> Ohne Kurzkontakte Stichtag 31.12. des jeweiligen Statistikjahres.
          </Typography>
        </Grid>
        <Grid item xs={7}>
          <Typography fontSize='0.9rem' fontWeight='bold'>
            3.2 Anzahl der Beratungen insgesamt{' '}
            <span style={{ fontSize: '0.75rem' }}>
              (alle erfolgten telefonischen/online und persönlichen Erst- und Folgeberatungen)
            </span>
            :
          </Typography>
        </Grid>
        <Grid item xs={5}>
          <Typography fontSize='0.9rem' fontWeight='bold' align='right' border='2px solid black' p='13px 5px'>
            {statistics.totalNumberOfMeasures}
          </Typography>
        </Grid>
      </Grid>
      <Grid container>
        <Grid item xs={7}>
          <Typography fontSize='0.9rem'>Telefonische Beratungen</Typography>
        </Grid>
        <Grid item xs={5}>
          <Typography
            fontSize='0.9rem'
            align='right'
            px='5px'
            borderTop='2px solid black'
            borderLeft='2px solid black'
            borderRight='2px solid black'
            className='fhbf-print-field-padding-small'
          >
            {statistics.numberOfTypeTelephoneConsultation}
          </Typography>
        </Grid>
        <Grid item xs={7}>
          <Typography fontSize='0.9rem'>Persönliche Beratungen</Typography>
        </Grid>
        <Grid item xs={5}>
          <Typography
            fontSize='0.9rem'
            align='right'
            px='5px'
            borderTop='2px solid black'
            borderLeft='2px solid black'
            borderRight='2px solid black'
            className='fhbf-print-field-padding-small'
          >
            {statistics.numberOfTypePersonalConsultation}
          </Typography>
        </Grid>
        <Grid item xs={7}>
          <Typography fontSize='0.9rem'>
            Online Beratungen <span className='font-size-small'>(alle Beratungen via digitaler Medien)</span>
          </Typography>
        </Grid>
        <Grid item xs={5}>
          <Typography
            fontSize='0.9rem'
            align='right'
            px='5px'
            borderTop='2px solid black'
            borderLeft='2px solid black'
            borderRight='2px solid black'
            className='fhbf-print-field-padding-small'
          >
            {statistics.numberOfTypeOnlineConsultation}
          </Typography>
        </Grid>
        <Grid item xs={7}>
          <Typography fontSize='0.9rem'>
            Zusätzliche Kurzkontakte{' '}
            <span className='font-size-small'>(z.B. Terminvereinbarungen, versuchte Kontaktaufnahmen u.ä.)</span>
          </Typography>
        </Grid>
        <Grid item xs={5}>
          <Typography
            fontSize='0.9rem'
            align='right'
            px='5px'
            borderTop='2px solid black'
            borderLeft='2px solid black'
            borderRight='2px solid black'
            className='fhbf-print-field-padding'
          >
            {statistics.numberOfContactAttempts}
          </Typography>
        </Grid>
        <Grid item xs={7}>
          <Typography fontSize='0.9rem'>
            <u>Davon</u> Beratungen mit Dolmetschung
          </Typography>
        </Grid>
        <Grid item xs={5}>
          <Typography
            fontSize='0.9rem'
            align='right'
            px='5px'
            borderTop='2px solid black'
            borderLeft='2px solid black'
            borderRight='2px solid black'
            className='fhbf-print-field-padding-small'
          >
            {statistics.numberOfConsultationWithTranslator}
          </Typography>
        </Grid>
        <Grid item xs={7}>
          <Typography fontSize='0.9rem'>
            <u>Davon</u> aufsuchenden Beratungen
          </Typography>
        </Grid>
        <Grid item xs={5}>
          <Typography
            fontSize='0.9rem'
            align='right'
            px='5px'
            borderTop='2px solid black'
            borderLeft='2px solid black'
            borderRight='2px solid black'
            className='fhbf-print-field-padding-small'
          >
            {statistics.numberOfTypeOutreachConsultation}
          </Typography>
        </Grid>
        <Grid item xs={12}>
          <Divider sx={{ borderBottomWidth: 2, borderColor: '#000000' }} />
          <Typography fontSize='0.75rem'>
            <b>Hinweis:</b> Die Gesamtzahl der Beratungen unter 3.2. umfasst alle telefonischen/online und persönlichen
            Beratungen. Die weiteren Kategorien werden lediglich speziell ausgewiesen!
          </Typography>
          <Divider sx={{ borderBottomWidth: 2, borderColor: '#000000' }} />
        </Grid>
      </Grid>
      <div className='pagebreak'></div>
      <Grid container rowSpacing={2}>
        <Grid item xs={12}>
          <Typography fontSize='0.9rem' fontWeight='bold'>
            3.3 Anzahl der Frauen mit x Beratungsterminen:n
          </Typography>
        </Grid>
      </Grid>
      <Grid container>
        <Grid item xs={7}>
          <Typography fontSize='0.9rem'>
            1 Beratungstermin <span style={{ fontSize: '0.8rem' }}>(Erstberatung mit Beratungsinhalt)</span>
          </Typography>
        </Grid>
        <Grid item xs={5}>
          <Typography
            fontSize='0.9rem'
            align='right'
            px='5px'
            py='15px'
            borderTop='2px solid black'
            borderLeft='2px solid black'
            borderRight='2px solid black'
          >
            {statistics.numberOfWomenWithOneMeasure}
          </Typography>
        </Grid>
        <Grid item xs={7}>
          <Typography fontSize='0.9rem'>
            2 Beratungstermine{' '}
            <span style={{ fontSize: '0.8rem' }}>(d.h. Erstberatung mit Beratungsinhalt und eine Folgeberatung)</span>
          </Typography>
        </Grid>
        <Grid item xs={5}>
          <Typography
            fontSize='0.9rem'
            align='right'
            px='5px'
            py='15px'
            borderTop='2px solid black'
            borderLeft='2px solid black'
            borderRight='2px solid black'
          >
            {statistics.numberOfWomenWithTwoMeasures}
          </Typography>
        </Grid>
        <Grid item xs={7}>
          <Typography fontSize='0.9rem'>
            3 Beratungstermine{' '}
            <span style={{ fontSize: '0.8rem' }}>(d.h. Erstberatung mit Beratungsinhalt und zwei Folgeberatungen)</span>
          </Typography>
        </Grid>
        <Grid item xs={5}>
          <Typography
            fontSize='0.9rem'
            align='right'
            px='5px'
            py='15px'
            borderTop='2px solid black'
            borderLeft='2px solid black'
            borderRight='2px solid black'
          >
            {statistics.numberOfWomenWithThreeMeasures}
          </Typography>
        </Grid>
        <Grid item xs={7}>
          <Typography fontSize='0.9rem'>
            4 Beratungstermine{' '}
            <span style={{ fontSize: '0.8rem' }}>(d.h. Erstberatung mit Beratungsinhalt und zwei Folgeberatungen)</span>
          </Typography>
        </Grid>
        <Grid item xs={5}>
          <Typography
            fontSize='0.9rem'
            align='right'
            px='5px'
            py='15px'
            borderTop='2px solid black'
            borderLeft='2px solid black'
            borderRight='2px solid black'
          >
            {statistics.numberOfWomenWithFourMeasures}
          </Typography>
        </Grid>
        <Grid item xs={7}>
          <Typography fontSize='0.9rem'>
            5 Beratungstermine und mehr
            <span style={{ fontSize: '0.8rem' }}>(d.h. Erstberatung mit Beratungsinhalt und zwei Folgeberatungen)</span>
          </Typography>
        </Grid>
        <Grid item xs={5}>
          <Typography fontSize='0.9rem' align='right' px='5px' py='15px' border='2px solid black'>
            {statistics.numberOfWomenWithFiveAndMoreMeasures}
          </Typography>
        </Grid>
      </Grid>
      <Grid container rowSpacing={2}>
        <Grid item xs={12}>
          <Typography fontSize='0.9rem'>
            Nennung der hauptsächlichen Gründe, warum von der vorgesehenen Regelberatungszahl abgewichen wurde und
            mindestens 5 Beratungstermine erforderlich waren:
          </Typography>
        </Grid>
        <Grid item xs={12}>
          <TextField
            label='Begründung und Anzahl der Termine pro Frau'
            multiline
            variant='standard'
            fullWidth
            rows={3}
          ></TextField>
        </Grid>
      </Grid>
      <Grid container>
        <Grid item xs={7}>
          <Typography fontSize='0.9rem' fontWeight='bold'>
            3.4 Anzahl der Follow up-Beratungen
          </Typography>
        </Grid>
        <Grid item xs={5}>
          <Typography
            fontSize='0.9rem'
            align='right'
            px='5px'
            borderTop='2px solid black'
            borderLeft='2px solid black'
            borderRight='2px solid black'
          >
            {statistics.numberOfTypeFollowUp}
          </Typography>
        </Grid>
        <Grid item xs={12}>
          <Divider sx={{ borderBottomWidth: 2, borderColor: '#000000' }} />
          <Typography fontSize='0.75rem'>
            <b>Hinweis:</b> Mit Zustimmung der Frau erfolgte erneute Kontaktaufnahme (nach maximal 2 Monaten) als
            aktualisierendes Abklärungs-/Beratungsgespräch.
          </Typography>
          <Divider sx={{ borderBottomWidth: 2, borderColor: '#000000' }} />
        </Grid>
      </Grid>
      <Grid container rowSpacing={2} mt={1}>
        <Grid item xs={7}>
          <Typography fontSize='0.9rem' fontWeight='bold'>
            3.5 Anzahl der Frauen, die keinen Beratungswunsch hatten
          </Typography>
        </Grid>
        <Grid item xs={5}>
          <Typography fontSize='0.9rem' align='right' p='5px' border='2px solid black'>
            {statistics.numberOfWomenWithNoConsultationRequest}
          </Typography>
        </Grid>
      </Grid>
    </Paper>
  );
}

export default Case;
